'use client'

import { type Post, type Media } from '@/payload-types'
import React, { useMemo, useState } from 'react'
import Link from 'next/link'
import { Caption } from '@/components/ui/caption'
import { Subheading } from '@/components/ui/subheading'
import { cn } from '@/utilities/ui'
import { Label } from '@/components/ui/label'
import { Heading } from '@/components/ui/heading'
import { Button } from '@/components/ui/Button'
import { getCMSLinkHref } from '@/utilities/getCMSLinkHref'
import { AuthorGroup } from '@/blocks/common/FeaturedPosts/AuthorGroup'
import { getAuthors } from '@/utilities/authors'
import { PixelatedImage } from '@/components/PixelatedImage'

export const PostCardLarge: React.FC<{
  post: Post
  className?: string
  card?: boolean
  aspectRatio?: '3/4' | '4/3' | '2/1'
  showAuthor?: boolean
  pixelSize?: number
  defaultState?: 'pixelated' | 'original'
}> = ({
  post,
  className,
  card = true,
  aspectRatio = '3/4',
  showAuthor = false,
  pixelSize = 24,
  defaultState = 'original',
}) => {
  const [isHovering, setIsHovering] = useState(false)
  const heroImage =
    post.heroImage && typeof post.heroImage === 'object' ? (post.heroImage as Media) : null
  const firstCategory =
    post.categories?.[0] && typeof post.categories[0] === 'object' ? post.categories[0] : null

  const authors = getAuthors(post)

  return (
    <Link
      href={
        getCMSLinkHref({
          type: 'reference',
          reference: {
            relationTo: 'posts',
            value: post,
          },
        })!
      }
      className={cn('group block w-full flex-none', card && 'bg-paper-2', className)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        className={cn(
          'flex-none relative overflow-hidden',
          aspectRatio === '3/4' && 'aspect-[3/4]',
          aspectRatio === '4/3' && 'aspect-[4/3]',
          aspectRatio === '2/1' && 'aspect-[2/1]',
        )}
      >
        <BoxesLabel label={firstCategory?.title || ''} />
        {heroImage && (
          <PixelatedImage
            src={heroImage.url || ''}
            alt={heroImage.alt || ''}
            aspectRatio={aspectRatio}
            pixelSize={pixelSize}
            defaultState={defaultState}
            isHovering={isHovering}
            className="w-full h-full"
          />
        )}
      </div>
      <div className={cn(card ? 'py-[30px] px-[30px]' : 'pt-[30px]')}>
        <div className="flex flex-col gap-[20px]">
          {showAuthor && <AuthorGroup authors={authors} />}
          <div className={cn('flex-1 flex flex-col gap-[20px] items-start')}>
            <Label className="uppercase text-olive-1/50 font-medium">
              {new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </Label>
            <Heading size="h3">{post.title}</Heading>
            <Button
              size="default"
              variant="punch"
              className="group-hover:underline"
              linkIcon="internal"
            >
              Read more
            </Button>
          </div>
        </div>
      </div>
    </Link>
  )
}

const colors = ['zest', 'punch', 'highlighter', 'slime', 'powder']
function BoxesLabel({ label }: { label: string }) {
  const shuffledColors = useMemo(() => colors.sort(() => Math.random() - 0.5), [])
  const randomTwoIndicies = useMemo(() => {
    // random between 1 and 4
    const randomIndex0 = Math.floor(Math.random() * 4) + 1
    const randomIndex1 = Math.floor(Math.random() * 4) + 1
    return [randomIndex0, randomIndex1]
  }, [shuffledColors])
  return (
    <div className="absolute z-10 top-0 left-0">
      <div className="flex">
        <Box colors={shuffledColors} index={1} randomTwoIndicies={randomTwoIndicies} />
        <Box colors={shuffledColors} index={2} randomTwoIndicies={randomTwoIndicies} />
      </div>
      <div className="flex">
        <Box colors={shuffledColors} index={3} randomTwoIndicies={randomTwoIndicies} />
        <Box colors={shuffledColors} index={0} randomTwoIndicies={randomTwoIndicies}>
          <Caption className="uppercase font-bold text-olive-1">{label}</Caption>
        </Box>
      </div>
      <div className="flex">
        <Box colors={shuffledColors} index={4} randomTwoIndicies={randomTwoIndicies} />
      </div>
    </div>
  )
}

function Box({
  colors,
  index,
  randomTwoIndicies,
  children,
}: {
  colors: string[]
  index: number
  randomTwoIndicies: number[]
  children?: React.ReactNode
}) {
  return (
    <div
      className={cn(
        children ? 'h-8 px-3 flex items-center' : 'w-8 h-8',
        randomTwoIndicies.includes(index) || index === 0 ? `bg-${colors[index]}` : 'bg-transparent',
      )}
    >
      {children}
    </div>
  )
}
